if (!customElements.get('video-popup')) {
  class VideoPopup extends HTMLElement {
    static activeInstance = null;

    constructor() {
      super();
      this.trigger = this.querySelector('.video-popup-trigger');
      this.activeModal = null;
      this.activeContent = null;
      this.isTrapActive = false;
      this.isClosing = false;
      this.isOpening = false;
      this.openingCancelled = false;
      this.restoreFocusOnClose = true;
      this.handleTriggerClick = this.open.bind(this);
      this.handlePosterError = this.handlePosterError.bind(this);
      this.handleDependencyAvailability = this.refreshAvailability.bind(this);
    }

    connectedCallback() {
      if (!this.trigger) {
        return;
      }

      this.trigger.addEventListener('click', this.handleTriggerClick);
      this.querySelectorAll('.video-popup-trigger-media img').forEach((image) => {
        image.addEventListener('error', this.handlePosterError);
      });
      this.refreshAvailability();
      window.requestAnimationFrame(this.handleDependencyAvailability);
      window.addEventListener('load', this.handleDependencyAvailability, { once: true });
    }

    disconnectedCallback() {
      if (this.trigger) {
        this.trigger.removeEventListener('click', this.handleTriggerClick);
      }
      this.querySelectorAll('.video-popup-trigger-media img').forEach((image) => {
        image.removeEventListener('error', this.handlePosterError);
      });
      window.removeEventListener('load', this.handleDependencyAvailability);
      if (VideoPopup.activeInstance === this && this.isOpening) {
        this.openingCancelled = true;
      } else if (VideoPopup.activeInstance === this && this.activeModal) {
        this.closeModal(false);
      } else {
        this.clearLocalState();
      }
    }

    hasModalDependencies() {
      return Boolean(
        window.$s
        && typeof window.$s.openModal === 'function'
        && typeof window.$s.trapFocus === 'function'
        && typeof window.$s.removeTrapFocus === 'function'
        && window.tingle
      );
    }

    getActiveTemplate() {
      if (!window.$s || !window.$s.media) {
        return null;
      }

      const source = window.$s.media.isMediumDown ? 'mobile' : 'desktop';
      return this.querySelector(`[data-video-popup-template="${source}"]`);
    }

    hasActiveMediaTemplate() {
      const template = this.getActiveTemplate();
      return Boolean(template && template.content && template.content.querySelector('video, iframe'));
    }

    refreshAvailability() {
      if (!this.isConnected) {
        return;
      }
      this.setUnavailable(!this.hasModalDependencies() || !this.hasActiveMediaTemplate());
    }

    setUnavailable(isUnavailable) {
      if (!this.trigger) {
        return;
      }

      this.trigger.disabled = isUnavailable;
    }

    handlePosterError(event) {
      event.currentTarget.hidden = true;
    }

    createContent() {
      const template = this.getActiveTemplate();
      if (!template || !template.content) {
        return null;
      }

      const content = document.createElement('div');
      content.className = 'video-popup-modal-content';
      content.appendChild(template.content.cloneNode(true));

      return content;
    }

    open() {
      if (this.activeModal || !this.trigger || !this.hasModalDependencies() || !this.hasActiveMediaTemplate()) {
        if (!this.activeModal) {
          this.setUnavailable(true);
        }
        return;
      }

      const previousInstance = VideoPopup.activeInstance;
      if (previousInstance && previousInstance !== this) {
        previousInstance.closeModal(true);
        if (VideoPopup.activeInstance === previousInstance) {
          return;
        }
      }

      const content = this.createContent();
      if (!content) {
        this.setUnavailable(true);
        return;
      }

      this.setUnavailable(false);
      this.activeContent = content;
      VideoPopup.activeInstance = this;
      this.isOpening = true;
      this.openingCancelled = false;
      const popup = this;

      try {
        const modal = window.$s.openModal(content, 'video-popup-modal', {
          onOpen: function() {
            this.modal.id = popup.dataset.dialogId;
            this.modal.setAttribute('aria-label', popup.dataset.dialogLabel);
            window.$s.trapFocus(this.modal);
            popup.isTrapActive = true;
            popup.bindMediaFailureHandlers();
            return true;
          },
          onClose: () => {
            this.completeCleanup();
            return true;
          }
        });
        this.activeModal = modal;
        this.isOpening = false;
        if (this.openingCancelled || !this.isConnected) {
          this.destroyModalFallback(modal, false);
          return;
        }
      } catch (error) {
        this.isOpening = false;
        const orphanModal = window.modal && window.modal.modal && window.modal.modal.classList.contains('video-popup-modal')
          ? window.modal
          : null;
        this.destroyModalFallback(orphanModal, true);
        this.setUnavailable(true);
        return;
      }

      const iframe = content.querySelector('iframe[data-src]');
      if (iframe) {
        iframe.src = iframe.dataset.src;
      }

      const video = content.querySelector('video');
      if (video) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => this.showMediaFailure());
        }
      }
    }

    bindMediaFailureHandlers() {
      if (!this.activeContent) {
        return;
      }

      this.activeContent.querySelectorAll('video, iframe').forEach((media) => {
        media.addEventListener('error', () => this.showMediaFailure(), { once: true });
      });
    }

    showMediaFailure() {
      if (!this.activeContent || this.activeContent.querySelector('.video-popup-media-failure')) {
        return;
      }

      this.stopMedia();
      const failure = document.createElement('p');
      failure.className = 'video-popup-media-failure';
      failure.setAttribute('role', 'alert');
      failure.textContent = this.dataset.unavailableLabel;
      this.activeContent.replaceChildren(failure);
    }

    stopMedia() {
      if (!this.activeContent) {
        return;
      }

      const video = this.activeContent.querySelector('video');
      if (video) {
        try {
          video.pause();
          video.currentTime = 0;
        } catch (error) {
        }
      }

      const iframe = this.activeContent.querySelector('iframe');
      if (iframe) {
        try {
          iframe.src = 'about:blank';
        } catch (error) {
        }
      }
    }

    closeModal(restoreFocus) {
      if (this.isClosing) {
        return;
      }

      this.restoreFocusOnClose = restoreFocus;
      const modal = this.activeModal;
      if (!modal || VideoPopup.activeInstance !== this) {
        this.clearLocalState();
        return;
      }
      if (typeof modal.close !== 'function') {
        this.destroyModalFallback(modal, restoreFocus);
        return;
      }

      this.isClosing = true;
      try {
        modal.close();
      } catch (error) {
        this.destroyModalFallback(modal, restoreFocus);
      }
    }

    destroyModalFallback(modal, restoreFocus) {
      if (VideoPopup.activeInstance !== this || !modal || !modal.modal) {
        this.clearLocalState();
        return;
      }
      const modalElement = modal.modal;
      this.completeCleanup(restoreFocus);
      if (modal && typeof modal.destroy === 'function') {
        try {
          modal.destroy();
        } catch (error) {
        }
      }
      if (modalElement && modalElement.isConnected) {
        modalElement.remove();
      }
      if (document.body) {
        document.body.classList.remove('tingle-enabled');
        document.body.style.top = '';
      }
    }

    completeCleanup(forceRestoreFocus) {
      this.stopMedia();
      if (VideoPopup.activeInstance === this && this.isTrapActive && window.$s && typeof window.$s.removeTrapFocus === 'function') {
        window.$s.removeTrapFocus();
      }
      this.isTrapActive = false;

      const shouldRestoreFocus = forceRestoreFocus || this.restoreFocusOnClose;
      if (shouldRestoreFocus && this.trigger && this.trigger.isConnected) {
        this.trigger.focus();
      }

      this.clearLocalState();
    }

    clearLocalState() {
      this.activeModal = null;
      this.activeContent = null;
      this.isTrapActive = false;
      this.isClosing = false;
      this.isOpening = false;
      this.openingCancelled = false;
      if (VideoPopup.activeInstance === this) {
        VideoPopup.activeInstance = null;
      }
    }
  }

  customElements.define('video-popup', VideoPopup);
}
